import { RECEIVE_LAYOUT_CONFIG, type ReceiveFieldRow, type SheetOrigin } from "@/excel/speckle/receiveTypes";

const BASIC_NUMBER_VALIDATION_MIN = -1e100;
const BASIC_NUMBER_VALIDATION_MAX = 1e100;
const MAX_EXCEL_LIST_VALIDATION_LENGTH = 255;

type ValueValidationDescriptor = {
  rule: Excel.DataValidationRule;
  errorTitle: string;
  errorMessage: string;
};

function getSafeExcelListValidationSource(values: string[]): string | null {
  if (values.length === 0) {
    return null;
  }

  if (values.some((value) => value.trim() === "" || value.includes(",") || /[\r\n]/.test(value))) {
    return null;
  }

  const source = values.join(",");

  return source.length <= MAX_EXCEL_LIST_VALIDATION_LENGTH ? source : null;
}

function buildValueValidationDescriptor(fieldRow: ReceiveFieldRow): ValueValidationDescriptor | null {
  if (fieldRow.validationKind === "boolean") {
    return {
      rule: {
        list: {
          inCellDropDown: true,
          source: "TRUE,FALSE",
        },
      },
      errorTitle: "Invalid boolean",
      errorMessage: fieldRow.validationMessage,
    };
  }

  if (fieldRow.validationKind === "enum") {
    const source = getSafeExcelListValidationSource(fieldRow.allowedValues);

    if (!source) {
      return null;
    }

    return {
      rule: {
        list: {
          inCellDropDown: true,
          source,
        },
      },
      errorTitle: "Invalid value",
      errorMessage: fieldRow.validationMessage,
    };
  }

  if (fieldRow.validationKind === "number") {
    return {
      rule: {
        decimal: {
          operator: Excel.DataValidationOperator.between,
          formula1: fieldRow.minimum ?? BASIC_NUMBER_VALIDATION_MIN,
          formula2: fieldRow.maximum ?? BASIC_NUMBER_VALIDATION_MAX,
        },
      },
      errorTitle: "Invalid number",
      errorMessage: "Enter a numeric value.",
    };
  }

  return null;
}

export function applyBasicValueValidations(
  worksheet: Excel.Worksheet,
  tableOrigin: SheetOrigin,
  fieldRows: ReceiveFieldRow[]
): void {
  for (const fieldRow of fieldRows) {
    const valueCell = worksheet.getRangeByIndexes(
      tableOrigin.startRow + fieldRow.rowId,
      tableOrigin.startColumn + RECEIVE_LAYOUT_CONFIG.valueColumnOffset,
      1,
      1
    );

    valueCell.dataValidation.clear();

    if (!fieldRow.editable || fieldRow.validationKind === "none") {
      continue;
    }

    valueCell.dataValidation.ignoreBlanks = true;

    const descriptor = buildValueValidationDescriptor(fieldRow);

    if (!descriptor) {
      continue;
    }

    valueCell.dataValidation.rule = descriptor.rule;

    valueCell.dataValidation.prompt = {
      showPrompt: true,
      title: "Expected value",
      message: fieldRow.validationMessage,
    };

    valueCell.dataValidation.errorAlert = {
      showAlert: true,
      style: Excel.DataValidationAlertStyle.stop,
      title: descriptor.errorTitle,
      message: descriptor.errorMessage,
    };
  }
}
